<template>
  <div id="app">
    <!-- Theme -->
    <div v-if="christmas">
      <ChristmasTheme :density="40" />
      <audio v-if="playMusic" autoplay loop ref="audioPlayer">
        <source src="https://resource.pafcpic.net/assets/pia/sounds/papsi_christmas.mp3" type="audio/mpeg" />
      </audio>
    </div>
    <div v-if="valentines">
      <ValentinesTheme :density="10" />
      <audio v-if="playMusic" autoplay loop ref="audioPlayer">
        <source src="https://resource.pafcpic.net/assets/pia/sounds/papsi_valentines.mp3" type="audio/mpeg" />
      </audio>
    </div>
    <div v-if="lunar">
      <LunarNewYear :density="20" />
      <audio v-if="playMusic" autoplay loop ref="audioPlayer">
        <source src="https://resource.pafcpic.net/assets/pia/sounds/papsi_lunar.mp3" type="audio/mpeg" />
      </audio>
    </div>
    <div v-if="independence">
      <IndependenceTheme />
    </div>
    <div v-if="halloween">
      <HalloweenTheme />
    </div>

    <!-- -->

    <LoadingScreenSection class="z-70" v-if="this.is_loading" />
    <ErrorSection class="z-60" v-if="this.maintenance_mode == false && this.error_mode" :is_error="true" />

    <router-view v-slot="{ Component }">
      <transition>
        <component :is="Component" v-if="!this.is_loading" :key="$route.fullPath" @logout="logout"
          @checkauth="checkAuth" @toggleRating="toggleRating" />
      </transition>
    </router-view>

    <div v-if="this.development_mode"
      class="fixed text-xl text-white font-semibold bg-error papsi-test p-2 rounded uppercase" style="z-index: 999999;">
      Dev</div>
    <div class="fixed text-sm text-primary z-30 papsi-version" v-text="version"></div>

    <!-- Kiosk Logout -->
    <transition name="slide-in">
      <ModalControl :is_visible="this.show_kiosk_logout" style="z-index: 1000;">
        <div class="flex flex-col justify-center pt-10 px-10">
          <LabelControl type="dynamic" class="text-grey-darkest font-bold text-6xl mb-8 uppercase text-center"
            content="Branch Log Out" />
          <p class="text-grey-darkest text-4xl align-center justify-center text-center">
            {{ $t('msg011') }}</p>
          <div class="flex flex-row justify-center align-center items-center">
            <ButtonControl text="Cancel" class="px-8 mr-5" theme="no" @click="this.toggleKioskLogoutModal()" />
            <ButtonControl text="Log Out" theme="error" @click="this.kioskLogout()" />
          </div>
        </div>
      </ModalControl>
    </transition>

    <!-- Existing Session -->
    <transition name="slide-in">
      <ModalControl :is_visible="this.has_existing_session">
        <SessionTimeoutControl />
      </ModalControl>
    </transition>

    <!-- Session Expired -->
    <transition name="slide-in">
      <ModalControl v-if="is_modal_open" :is_visible="is_modal_open" @close="toggleModal">
        <div class="flex flex-col justify-center align-center items-center p-8">
          <img :src="getIcon('info-blue')" class="mb-10">
          <LabelControl type="dynamic" class="text-grey-darkest font-bold text-5xl mb-8" content="Still with us?" />
          <p class="mb-10 text-grey-darkest text-4xl align-center justify-center text-center">
            {{ $t('msg012') }} <span class="font-bold">{{ modal_countdown }}
              {{ $t('msg013') }}</span> {{ $t('msg014') }}</p>
          <div class="flex flex-row justify-center align-center items-center">
            <ButtonControl text="Log out" class="px-8 mr-5" theme="no" @click="handleModalOk" />
            <ButtonControl text="Stay logged in" theme="login" @click="toggleModal" />
          </div>
        </div>
      </ModalControl>
    </transition>

  </div>
</template>

<script>
import ErrorSection from './components/sections/ErrorSection.vue';
import LoadingScreenSection from './components/sections/LoadingScreenSection.vue';
import LabelControl from './components/controls/LabelControl.vue';
import ModalControl from './components/controls/ModalControl.vue';
import ButtonControl from './components/controls/ButtonControl.vue';
import RatingControl from './components/controls/RatingControl.vue';
import SessionTimeoutControl from './components/controls/SessionTimeoutControl.vue';
import mixin from './mixins/controls';
import preload from './mixins/preload';
import user from './services/user';
import kiosk from './services/kiosk';

import ChristmasTheme from './components/themes/ChristmasTheme.vue';
import ValentinesTheme from './components/themes/ValentinesTheme.vue';
import LunarNewYear from './components/themes/LunarNewYear.vue';
import IndependenceTheme from './components/themes/IndependenceTheme.vue';
import HalloweenTheme from './components/themes/HalloweenTheme.vue';

export default {
  name: 'App',
  mixins: [mixin, preload],
  emits: ['checkauth'],
  components: {
    ErrorSection,
    LoadingScreenSection,
    ModalControl,
    LabelControl,
    ButtonControl,
    RatingControl,
    SessionTimeoutControl,
    ChristmasTheme,
    ValentinesTheme,
    LunarNewYear,
    IndependenceTheme,
    HalloweenTheme
  },
  data() {
    return {
      device_id: new URLSearchParams(window.location.search).get('device_id'),
      maintenance_mode: process.env.VUE_APP_MAINTENANCE_MODE === 'true',
      development_mode: process.env.VUE_APP_DEVELOPMENT_MODE === 'true',
      balance_delay: parseInt(process.env.VUE_APP_BALANCE_FETCH_DELAY, 10),
      modal_delay: parseInt(process.env.VUE_APP_MODAL_DELAY, 10),
      idle_delay: parseInt(process.env.VUE_APP_IDLE_DELAY, 10), // test later
      idle_time: parseInt(process.env.VUE_APP_IDLE_TIMEOUT, 10),
      modal_countdown: parseInt(process.env.VUE_APP_MODAL_TIMEOUT, 10) / 1000, // Countdown in seconds
      show_kiosk_logout: false,
      error_mode: false,
      has_existing_session: false,
      is_loading: false,
      idle_timeout: null,
      is_modal_open: false,
      modal_timeout: null,
      modal_interval: null,
      version: null,
      has_preloaded: false,
      excluded_routes: [
        'start-page',
        'kiosk-login-page',
        'login-page',
        'enter-pin-page',
        'papsi-mobile-login-page',
        'account-serial-number',
        'login-page',
        'scan-qr-page',
        'error-page',
        'attract-page',
        'choose-lang-page',
        'enroll-pin-page',
      ],
      extended_idle_routes: [
        'transactions-page',
        'member-info-page',
      ],
    };
  },
  watch: {
    $route(to) {
      if (!this.isExcludedRoute(to.name)) {
        this.startIdleTimer();
      } else {
        clearTimeout(this.idle_timeout);
        this.is_modal_open = false;
      }
    },
  },
  computed: {
    isMonth() {
      return (month) => new Date().getMonth() === month;
    },
    christmas() {
      return this.isMonth(11);
    },
    valentines() {
      const today = new Date();
      const month = today.getMonth();
      const day = today.getDate();
      return month === 1 && day >= 10 && day <= 19; // February (month index 1) from 10th to 19th
    },

    lunar() {
      const today = new Date();
      const month = today.getMonth();
      const day = today.getDate();
      return month === 0 && day >= 27 && day <= 31;
    },
    independence() {
      const today = new Date();
      const month = today.getMonth();
      const day = today.getDate();
      return month === 5 && day >= 10 && day <= 14;
    },
    halloween() {
      const today = new Date();
      const month = today.getMonth();
      const day = today.getDate();
      return month === 9 && day >= 25 && day <= 31;
    },
    playMusic() {
      return this.$route.name === "start-page";
    },
  },
  mounted() {
    this.version = 'Version ' + process.env.VUE_APP_VERSION;
      const url = new URL(window.location.href);

    if (!url.searchParams.has('device_id')) {
      this.$router.push({ name: 'error' });
      return;
    }

    if (!this.has_preloaded) {
      this.setLoading(true);
      this.preloadData()
        .then(() => {

          if (!this.device_id) { // save device id from URL to store
            this.$router.push({ name: 'error' });
          } else {
            this.$store.state.branch.device_id = this.device_id;
          }

          if (this.$cookies.isKey('session_id')) {
            kiosk.setSessionID(this.$cookies.get('session_id'));
            this.restoreBranchData();
            this.setLoading(false);
          } else {
            this.$router.push({ name: 'kiosk-login-page' });
            this.setLoading(false);
          }

          if (this.$cookies.isKey('hash') && !this.$store.state.user.hash) {
            this.setLoading(true);
            this.recoverSession()
              .then(() => {
                this.has_preloaded = true;
                this.setLoading(false);
              })
              .catch((error) => {
                console.log("Error in recovering user session:", error);
                this.resetUserData();
                this.clearUserCookie();
                this.$router.push({ name: 'start-page', query: { device_id: this.$store.state.branch.device_id } });
                this.setLoading(false);
              });
          }
        })
        .catch((error) => {
          console.error("Error during preloading:", error);
          this.setLoading(false);
        });
    }

    window.addEventListener('mousemove', this.startIdleTimer);
    window.addEventListener('keydown', this.startIdleTimer);
    window.addEventListener('touchstart', this.startIdleTimer);

    let keys_pressed = {};

    window.addEventListener('keydown', (event) => {
      keys_pressed[event.key] = true;
      if (keys_pressed['Control'] && keys_pressed[';'] && this.$cookies.isKey('session_id')) {
        this.toggleKioskLogoutModal();
        keys_pressed = {};
      }
    });

    window.addEventListener('keyup', (event) => {
      delete keys_pressed[event.key];
    });

    // Initialize the idle timer
    if (!this.isExcludedRoute()) {
      this.startIdleTimer();
    }

    if (this.$refs.audioPlayer) {
      this.$refs.audioPlayer.volume = 0.2; // Set volume (0.0 to 1.0)
    }
  },

  beforeUnmount() {
    window.removeEventListener('mousemove', this.startIdleTimer);
    window.removeEventListener('keydown', this.startIdleTimer);
    window.removeEventListener('touchstart', this.startIdleTimer);

    // Clear the idle timer
    clearTimeout(this.idle_timeout);
  },

  provide() {
    return {
      setLoading: this.setLoading,
      setError: this.setError,
      setMemberInfoLoading: this.setMemberInfoLoading,
    };
  },

  methods: {
    recoverSession() {
      return new Promise((resolve, reject) => {
        kiosk.setSessionID(this.$cookies.get('session_id'));
        user.recoverSession(this.$cookies.get('hash')).then((response) => {
          this.restoreUserData(response.data.data);
          resolve();
        }).catch((error) => {
          console.log("Error in recovering user session:", error);
          this.resetUserData();
          this.clearUserCookie();
          reject(error);
        });
      });
    },

    retrieveBalances() {
      this.setLoading(true);
      user.fetchBalances(this.$store.state.user.hash, this.$store.state.user.uuid, this.$store.state.user.balance_ref).then((response) => {
        const balances = response.data.data;
        Object.entries(balances).forEach(([code, { total_balance, total_records, as_of }]) => {
          this.$store.commit('addBalance', { code: code, balance: total_balance, records: total_records, as_of: as_of  });
        });
      }).catch((error) => {
        if (error.response && error.response.status === 403) {
          this.resetUserData();
          this.clearUserCookie();
          this.has_existing_session = true;
          this.toggleExistingSessionModal();
        }
        else if (error.response && error.response.status === 417) {
          let timer = setTimeout(() => {
            this.retrieveBalances();
            clearTimeout(timer);
          }, this.balance_delay);
        }
        else {
          this.showError('Failed to retrieve balances. Please log in again.');
          this.logout();
        }
      });
      this.setLoading(false);
    },

    toggleKioskLogoutModal() {
      this.show_kiosk_logout = !this.show_kiosk_logout;
    },

    kioskLogout() {
      this.show_kiosk_logout = false;
      // console.log("Logging out kiosk...");
      this.$store.commit('setLanguage', 'en');
      this.$i18n.locale = 'en';
      this.setLoading(true);
      // kiosk.destroySession(this.$store.state.user.hash).then(() => {
      kiosk.destroySession().then(() => {
        this.$router.push({ name: 'kiosk-login-page', query: { device_id: this.$store.state.branch.device_id } });
        this.resetBranchData();
        this.resetUserData();
        this.clearUserCookie();
        this.clearBranchCookie();
        this.showSuccess('Kiosk logout successful!');
        this.show_kiosk_logout = false;
      }).catch((error) => {
        this.showError(error.response.data.message);
        this.show_kiosk_logout = false;
      }).finally(() => {
        this.setLoading(false);
      });
    },

    setLoading(status) {
      this.is_loading = status;
    },
    
    setMemberInfoLoading(status) {
      this.is_member_info_loading = status;
    },

    setError(status) {
      this.error_mode = status;
    },

    checkAuth(user_required = true) {

      if (!this.$cookies.isKey('session_id')) { // if kiosk is not logged in, early return to the kiosk login page
        this.$router.push({ name: 'kiosk-login-page' });
        return;
      }

      const device_id = { query: { device_id: this.$store.state.branch.device_id } };
      const allowed_routes = ['start-page', 'choose-lang-page', 'login-page', 'enter-pin-page', 'papsi-mobile-login-page', 'account-serial-numberlogin-page', 'scan-qr-page', 'enroll-pin-page'];

      // if page requires user to be logged in
      if (user_required) {
        if (!this.$cookies.isKey('hash')) { // if user is not logged in, return to the start page
          this.$router.push({ name: 'start-page', ...device_id });
          return;
        }
      } else {// if page does not require user to be logged in
        if (this.$cookies.isKey('hash')) { // if user is logged in, return to the services page
          this.$router.push({ name: 'services-page', ...device_id });
          return;
        }
        // if kiosk is logged in but user is not, redirect to start page excluding login pages
        if (this.$cookies.isKey('session_id')) {
          if (!allowed_routes.includes(this.$route.name)) {
            this.$router.push({ name: 'start-page', ...device_id });
            return;
          }
        }
      }
    },

    startIdleTimer() {
      clearTimeout(this.idle_timeout); // clear any previously set timeout 
      if (!this.development_mode && !this.isExcludedRoute() && !this.is_loading && !this.is_member_info_loading) { 
        let idle_time = parseInt(process.env.VUE_APP_IDLE_TIMEOUT, 10) || 10000;

        if (this.extended_idle_routes.includes(this.$route.name)) {
          idle_time += 15000; // Add 15 seconds for specific pages
        }

        this.idle_timeout = setTimeout(() => {
          this.is_modal_open = true;

          // Initialize modal countdown
          this.modal_countdown = parseInt(process.env.VUE_APP_MODAL_TIMEOUT, 10) / 1000; // the time in seconds that will auto logout the the user
          this.modal_interval = setInterval(() => {
            if (this.modal_countdown > 0) {
              this.modal_countdown -= 1;
            }
          }, this.modal_delay);

          // Set auto-logout timeout
          this.modal_timeout = setTimeout(() => {
            this.handleModalOk(); // Trigger logout
          }, this.idle_delay); // Auto logout
        }, idle_time); // idle detection
      }
    },

    isExcludedRoute() {
      const currentRoute = this.$route.name;
      return this.excluded_routes.includes(currentRoute);
    },

    toggleModal() {
      this.is_modal_open = !this.is_modal_open;

      if (!this.is_modal_open) {
        clearInterval(this.modal_interval);
        clearTimeout(this.modal_timeout);
      }
    },

    toggleRating() {
      this.is_rating_open = !this.is_rating_open;
    },

    handleModalOk() {
      this.is_modal_open = false;
      clearInterval(this.modal_interval);
      clearTimeout(this.modal_timeout);
      this.logout();
    },

    logout() {
      this.setLoading(true);
      // console.log("Logging out user...");
      this.$store.commit('setLanguage', 'en');
      this.$i18n.locale = 'en';
      this.is_modal_open = false;
      clearInterval(this.modal_interval);
      clearTimeout(this.modal_timeout);
      user.destroySession(this.$cookies.get('hash')).then(() => {
        this.resetUserData();
        this.clearUserCookie();
        this.$router.push({ name: 'start-page', query: { device_id: this.$store.state.branch.device_id } });
        this.setLoading(false);
      }).catch((error) => {
        if (error.response && error.response.status === 403) {
          this.resetUserData();
          this.clearUserCookie();
          this.setLoading(false);
          this.$router.push({ name: 'start-page', query: { device_id: this.$store.state.branch.device_id } });
          return;
        }
        this.showError(error.response.data.message);
        this.setLoading(false);
      });
    },

  },
}
</script>

<style>
body {
  font-family: 'Inter', sans-serif !important;
  overflow: hidden;
  touch-action: none;
  width: 100%;
}

.papsi-version {
  bottom: 10px;
  left: 10px;
}

.papsi-test {
  top: 20px;
  left: 20px;
}

button:focus {
  outline: none !important;
}

.papsi-lang {
  top: 20px;
  left: 20px;
}

.v-toast__item {
  width: auto !important;
  height: 5.2rem !important;
  border-radius: 10px !important;
}

.v-toast__item--error {
  background-color: #E5413E !important;
}

.v-toast__item--info {
  background-color: #2196F3 !important;
}

.v-toast__item--success {
  background-color: #016b00 !important;
}

.v-toast__icon {
  padding: 1.1rem !important;
  margin-left: 1.5rem !important;
}

.v-toast__text {
  padding: 0 2rem 0 1.5rem !important;
  font-size: 2rem;
}

.papsi-text::-webkit-scrollbar {
  width: 10px;
}

.papsi-text::-webkit-scrollbar-thumb {
  background: #dae1e7;
  border-radius: 5px;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.3s ease, opacity 0s ease;
}

.slide-in-enter-from {
  transform: translateY(-50px);
  opacity: 0;
}

.slide-in-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.slide-in-leave-to {
  transform: translateY(-50px);
  opacity: 0;
}

.bubble-wrapper {
  position: relative;
  animation: bubble-pop 0.5s ease-out;
}

.bubble-pointer {
  position: absolute;
  left: 20%;
  bottom: -15px;
  transform: translateX(-50%);
  border-left: 16px solid transparent;
  border-right: 36px solid transparent;
  border-top: 16px solid white;
  z-index: 2;
}

.bubble-shine {
  position: relative;
  overflow: hidden;
  background: white;
  isolation: isolate; /* Ensures the shine doesn't affect outside elements */
}

.bubble-shine::before {
  content: "";
  position: absolute;
  top: 0;
  left: -150%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0.5) 50%, /* Increased opacity for stronger shine */
    rgba(255, 255, 255, 0) 80%
  );
  animation: shine 4s infinite ease-in-out; /* Faster animation for a stronger visual impact */
  opacity: 1; /* Brighter overall */
  z-index: 1;
}

@keyframes shine {
  0% {
    transform: translateX(-150%);
  }
  100% {
    transform: translateX(150%);
  }
}

@keyframes bubble-pop {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  60% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
}

@media (min-width: 2500px) {
  h2 {
    font-size: 4rem !important;
  }

  .text-xl {
    font-size: 2.25rem !important;
  }

  .text-2xl {
    font-size: 2.5rem !important;
  }

  .text-3xl {
    font-size: 2.875rem !important;
  }

  .text-4xl {
    font-size: 3.25rem !important;
  }

  .text-5xl {
    font-size: 3.5rem !important;
  }

  .text-6xl {
    font-size: 4rem !important;
  }

  .text-7xl {
    font-size: 5rem !important;
  }

  .text-date {
    font-size: 2rem !important;
  }

  .papsi-mascot {
    width: 30rem !important;
  }

  .papsi-text {
    height: 60rem !important;
  }

  .papsi-container {
    height: 73rem !important;
    border-radius: 2rem !important;
  }

  .v-toast__item {
    width: auto !important;
    height: 8.2rem !important;
    border-radius: 30px !important;
  }

  .v-toast__icon {
    width: 3rem !important;
    height: 3rem !important;
    padding: 1.1rem !important;
    margin-left: 1.5rem !important;
  }

  .v-toast__text {
    padding: 0 2rem 0 1.5rem !important;
    font-size: 3rem;
  }

  .papsi-test {
    font-size: 2rem !important;
    padding: 0.5rem 1rem !important;
  }

  .papsi-icon {
    width: 60px !important;
    height: 60px !important;
  }
}
</style>
